import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Full search component — dual-fetch layout:
 * - Products from /search?type=product (Section Rendering API)
 * - Queries + collections from /search/suggest?resources[type]=query,collection (Predictive Search API)
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} searchLayout
 * @property {HTMLElement} searchResultsSidebar
 * @property {HTMLElement} searchResultsProducts
 * @property {HTMLElement} resetButton
 * @property {HTMLElement} [viewAllButton]
 * @extends {Component<Refs>}
 */
class FullSearchComponent extends Component {
  requiredRefs = ['searchInput', 'searchLayout', 'searchResultsSidebar', 'searchResultsProducts', 'resetButton'];

  /** @type {Map<string, {productsHtml: string, sidebarHtml: string}>} */
  #cache = new Map();

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  #recentlyViewedLoaded = false;

  /**
   * Snapshot of the body scroll-lock the search dialog applied on open, so it can be
   * restored if a quick-add opens on top and later releases the shared lock on close.
   * @type {{position: string, top: string, width: string} | null}
   */
  #searchBodyLock = null;

  get #minLength() {
    return parseInt(this.dataset.minLength ?? '3', 10);
  }

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    // Fresh controller each connect so listeners survive a disconnect/reconnect
    // (the previous one was aborted in disconnectedCallback).
    this.#controller = new AbortController();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    }

    // Quick-add opens as its own top-layer dialog on top of the search popup. Since both
    // share the same footprint and the quick-add backdrop is translucent, the search popup
    // bleeds through and reads as if the quick-add were nested/clipped inside it. While the
    // quick-add is open, flag the component so scoped CSS hides the search popup — the
    // quick-add then displays as a standalone overlay (as it does from a PLP).
    const quickAddDialog = document.getElementById('quick-add-dialog');
    if (quickAddDialog) {
      quickAddDialog.addEventListener(DialogOpenEvent.eventName, this.#handleQuickAddOpen, { signal });
      quickAddDialog.addEventListener(DialogCloseEvent.eventName, this.#handleQuickAddClose, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** @param {KeyboardEvent} event */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    // Defensive: if a quick-add close event was ever missed, clear the flag so the
    // search popup is never left hidden across a reopen.
    delete this.dataset.quickAddOpen;
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    // Snapshot the body scroll-lock the search just applied (position/top/width), so a
    // stacked quick-add releasing the shared lock on close can be undone (see below).
    const { position, top, width } = document.body.style;
    this.#searchBodyLock = { position, top, width };
    this.refs.searchInput.focus();
    this.#loadRecentlyViewed();
  };

  #handleQuickAddOpen = () => {
    this.dataset.quickAddOpen = 'true';
  };

  #handleQuickAddClose = () => {
    delete this.dataset.quickAddOpen;

    // The quick-add is its own DialogComponent; its close releases the body scroll-lock
    // entirely. While the search is still open underneath, restore the search's lock so
    // the background stays locked at its original scroll position.
    const lock = this.#searchBodyLock;
    const searchDialog = this.closest('dialog');
    if (lock && searchDialog instanceof HTMLDialogElement && searchDialog.open) {
      document.body.style.position = lock.position;
      document.body.style.top = lock.top;
      document.body.style.width = lock.width;
    }
  };

  /**
   * Populates the "Recently viewed" idle row from localStorage product ids.
   * Stays hidden when there are no viewed products.
   */
  async #loadRecentlyViewed() {
    const grid = this.refs.recentlyViewedGrid;
    const section = this.refs.recentlyViewed;
    if (!grid || !section || this.#recentlyViewedLoaded) return;
    this.#recentlyViewedLoaded = true;

    const ids = RecentlyViewed.getProducts();
    if (ids.length === 0) return;

    try {
      const url = new URL('/search', location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      const html = await sectionRenderer.getSectionHTML('full-search-products', false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const list = doc.querySelector('.predictive-search-results__wrapper-products');
      if (list && list.children.length > 0) {
        grid.replaceChildren(list);
        section.hidden = false;
      }
    } catch (error) {
      // Leave the recently-viewed row hidden on failure.
    }
  }

  clearRecentlyViewed() {
    RecentlyViewed.clearProducts();
    if (this.refs.recentlyViewed) this.refs.recentlyViewed.hidden = true;
  }

  search = debounce((event) => {
    if (!event.inputType) return;

    const term = this.refs.searchInput.value.trim();

    if (term.length < this.#minLength) {
      this.#clearResults();
      if (term.length === 0) {
        this.#hideResetButton();
      }
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(term);
  }, 200);

  /** @returns {string[]} Predictive resource types to request for the sidebar, per theme settings */
  get #sidebarResourceTypes() {
    const types = [];
    if (this.dataset.showSuggestions === 'true') types.push('query');
    if (this.dataset.showCategories !== 'false') types.push('collection');
    if (this.dataset.showPages === 'true') types.push('page');
    return types;
  }

  /** @param {string} term */
  async #getSearchResults(term) {
    const resourceTypes = this.#sidebarResourceTypes;
    const key = `${term.toLowerCase()}:${resourceTypes.join(',')}`;

    if (this.#cache.has(key)) {
      this.#renderResults(/** @type {{productsHtml: string, sidebarHtml: string}} */ (this.#cache.get(key)));
      return;
    }

    const abort = this.#createAbortController();

    // Products via Section Rendering API (full search engine, like biribox)
    const searchUrl = new URL('/search', location.origin);
    searchUrl.searchParams.set('q', term);
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('options[prefix]', 'last');

    try {
      let productsHtml, sidebarHtml;

      if (resourceTypes.length > 0) {
        // Sidebar suggestions via Predictive Search API (queries / collections / pages, per settings)
        const suggestUrl = new URL(Theme.routes.predictive_search_url, location.origin);
        suggestUrl.searchParams.set('q', term);
        suggestUrl.searchParams.set('resources[type]', resourceTypes.join(','));

        [productsHtml, sidebarHtml] = await Promise.all([
          sectionRenderer.getSectionHTML('full-search-products', false, searchUrl),
          sectionRenderer.getSectionHTML('full-search', false, suggestUrl),
        ]);
      } else {
        productsHtml = await sectionRenderer.getSectionHTML('full-search-products', false, searchUrl);
        sidebarHtml = null;
      }

      if (abort.signal.aborted) return;

      const result = { productsHtml, sidebarHtml };
      this.#cache.set(key, result);
      this.#renderResults(result);
    } catch (error) {
      if (!abort.signal.aborted) throw error;
    }
  }

  /** @param {{productsHtml: string, sidebarHtml: string | null}} result */
  #renderResults({ productsHtml, sidebarHtml }) {
    this.dataset.hasResults = 'true';
    morph(this.refs.searchResultsProducts, productsHtml);
    if (sidebarHtml) {
      morph(this.refs.searchResultsSidebar, sidebarHtml);
    } else {
      this.refs.searchResultsSidebar.innerHTML = '';
    }

    const hasSidebar = !!this.refs.searchResultsSidebar.querySelector('[data-search-sidebar]');
    this.refs.searchLayout.classList.toggle('full-search-results__layout--has-sidebar', hasSidebar);
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
  }

  #clearResults() {
    this.removeAttribute('data-has-results');
    this.refs.searchResultsProducts.innerHTML = '';
    this.refs.searchResultsSidebar.innerHTML = '';
    this.refs.searchLayout.classList.remove('full-search-results__layout--has-sidebar');
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  #resetSearch = () => {
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    this.#clearResults();
  };

  /** @param {KeyboardEvent} event */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (event.key === 'Enter') {
      const singleResultEl = this.refs.searchResultsProducts.querySelector('[data-single-result-url]');
      if (singleResultEl instanceof HTMLElement && singleResultEl.dataset.singleResultUrl) {
        event.preventDefault();
        window.location.href = singleResultEl.dataset.singleResultUrl;
        return;
      }

      if (!event.target.closest('a, button')) {
        event.preventDefault();
        const searchUrl = new URL(Theme.routes.search_url, location.origin);
        searchUrl.searchParams.set('q', this.refs.searchInput.value.trim());
        searchUrl.searchParams.set('options[prefix]', 'last');
        window.location.href = searchUrl.toString();
      }
    }
  };

  viewAllResults() {
    const term = this.refs.searchInput.value.trim();
    if (!term) return;
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', term);
    window.location.href = searchUrl.toString();
  }
}

if (!customElements.get('full-search-component')) {
  customElements.define('full-search-component', FullSearchComponent);
}
